/* ============================================================
   NOOK ROI Calculator — v2 Portfolio Suite
   ============================================================
   Four modes, one component.

   - Simple:        1 unit type, 1 rate, 1 occupancy. Entry-level.
   - Portfolio:     Multi-unit mix, operating costs, GOPPAR/RevPAR.
   - Institutional: Financing, hold-period cashflow, IRR, NPV, sensitivity.
   - Sessions:      Per-session economics (sauna + rest rooms) for an
                    accommodation operator. Off by default; opt-in per section.

   Translations are read from #nook-i18n (rendered by snippets/nook-i18n.liquid).
   All dynamic DOM is built via createElement / textContent — no innerHTML.

   Version: 2.2.0
   ============================================================ */

(function () {
  'use strict';

  /* ── i18n + currency ─────────────────────────────────────── */
  var I18N = readI18N();
  var T = I18N.roi || {};
  var LOCALE = String(I18N.locale || 'es').toLowerCase();
  /* Bilingual helper for strings the JS injects at runtime (labels/captions the
     server-side snippets don't yet carry). ES is authored natural, never calqued. */
  function L(es, en) { return LOCALE.indexOf('en') === 0 ? en : es; }
  var CURRENCY = I18N.currency || 'EUR';
  var CURRENCY_CONFIG = (I18N.currencyConfig && I18N.currencyConfig[CURRENCY]) || { rate: 1, rounding: 0 };
  var FX_RATE = CURRENCY_CONFIG.rate || 1;
  var FX_ROUND = CURRENCY_CONFIG.rounding || 0;

  /* ── Preset templates (Portfolio mode) ───────────────────────
     Occupancies re-centred to credible UK outdoor-stay levels (annualized
     typically 45–65%) so a preset click never re-prints absurd yields. */
  var PRESETS = {
    glamping: { rows: [{ unitIndex: 0, qty: 5, rate: 180, occ: 58 }, { unitIndex: 3, qty: 2, rate: 120, occ: 52 }] },
    boutique: { rows: [{ unitIndex: 0, qty: 3, rate: 230, occ: 62 }, { unitIndex: 3, qty: 1, rate: 150, occ: 56 }] },
    wellness: { rows: [{ unitIndex: 1, qty: 6, rate: 340, occ: 60 }, { unitIndex: 3, qty: 3, rate: 170, occ: 55 }] },
    estate:   { rows: [{ unitIndex: 0, qty: 4, rate: 170, occ: 56 }, { unitIndex: 1, qty: 1, rate: 300, occ: 58 }] }
  };

  var DEFAULT_SEEDS = {
    simple:        { rows: [{ unitIndex: 0, qty: 1, rate: 185, occ: 58 }] },
    portfolio:     { rows: [{ unitIndex: 0, qty: 4, rate: 185, occ: 58 }, { unitIndex: 3, qty: 1, rate: 115, occ: 52 }] },
    institutional: { rows: [{ unitIndex: 0, qty: 4, rate: 185, occ: 58 }, { unitIndex: 3, qty: 1, rate: 115, occ: 52 }] }
  };

  var DEFAULT_OPEX = {
    utilities: 8,        /* % of gross revenue */
    cleaningPerStay: 18, /* local currency per turnover (per stay, not per night) */
    avgLengthOfStay: 3,  /* nights per booking — divides cleaning events */
    platformFees: 12,    /* % of gross */
    management: 10,      /* % of gross */
    marketingPct: 5,     /* % of gross (direct-booking / OTA acquisition) */
    maintenance: 4,      /* % of gross (reserve) */
    ffeReservePct: 4,    /* % of gross (FF&E replacement reserve) */
    businessRates: 1200, /* local currency PER UNIT per year (fixed) */
    insurance: 900       /* local currency PER UNIT per year (fixed) */
  };

  /* Site works & connections — groundworks, piles, utilities, access.
     Default 40% of the pod subtotal; becomes a user-entered € amount once edited. */
  var DEFAULT_CAPEX = {
    siteWorksPct: 40,    /* % of pod subtotal when auto */
    siteWorksAuto: true, /* false once the operator enters their own € figure */
    siteWorksCents: 0    /* absolute override in local-currency cents (auto → derived) */
  };

  /* Sessions mode — per-session economics for guest wellness (sauna + rest rooms).
     Money defaults are authored in EUR and FX-converted at init. Conservative by
     construction: never tune these to shorten payback. */
  var DEFAULT_SESSIONS = {
    slotsPerDay: 4,
    revenuePerSlot: 60,       /* EUR per booked slot */
    daysPerWeek: 5,
    operatingWeeks: 48,
    utilisation: 40,          /* % of offered slots actually booked */
    resetCost: 12,            /* EUR — housekeeping reset, linen, consumables */
    energyCost: 3,            /* EUR, heat + water per slot. A placeholder the reader replaces:
                                 the NOOK Sauna's electrical load is confirmed per proposal
                                 (LINEUP_2026_FACTS §2.3), so no kW figure is derived here. */
    bookingFeePct: 10,        /* % of gross */
    fixedAnnual: 1500,        /* EUR / yr */
    maintenanceReserve: 1200, /* EUR / yr */
    /* The unit price, ex works, in cents. Derived from the unit_type blocks
       (market price overrides first, else the EUR price converted) until the
       operator types their own figure. Prices are ex works since the 2026
       dossier: transport, foundations and installation are quoted per address
       and live in `installQuote`, never inside this figure. */
    capexCents: 0,
    capexUserEdited: false,
    /* Transport, foundations and installation, as quoted for the reader's
       address. No published figure exists, so it starts at 0 and the field
       note asks for the quote. Never fill it with an invented number. */
    installQuote: 0,          /* EUR */
    /* Site services and permissions, the reader's own side of the scope
       table: bringing water, waste and power to the nearest point, any
       ground survey, access and permissions. */
    siteAllowance: 12000      /* EUR */
  };

  /* A 7-year payback is the underwriting threshold the results lead with. */
  var SESSIONS_TARGET_PAYBACK_YEARS = 7;

  var DEFAULT_FIN = {
    ltv: 0,             /* % */
    interestRate: 7,    /* % */
    amortYears: 20,
    discountRate: 8,    /* % */
    exitMultiple: 5,    /* × NOI — conservative going-in underwriting default (input editable toward the 10–12× market thesis) */
    holdYears: 5        /* institutional hold */
  };

  var HOLD_YEARS_CASHFLOW_TABLE = 10;
  var REVENUE_ESCALATOR = 0.03;
  var OPEX_ESCALATOR = 0.03; /* never below revenue's escalator */

  /* Scenario multipliers re-centred: base occ ~58% (glamping) →
     Conservative ≈45%, Ambitious ≈68% effective occupancy. */
  var SCENARIO_MULTS = {
    pessimistic: { adr: 0.85, occ: 0.78 },
    base:        { adr: 1.0,  occ: 1.0  },
    optimistic:  { adr: 1.15, occ: 1.17 }
  };

  /* Displayed scenario names (keys stay pessimistic/base/optimistic for markup
     compatibility). Conservative / Typical / Ambitious per the retune. */
  var SCENARIO_LABELS = {
    pessimistic: { label: L('Conservador', 'Conservative'), hint: L('ADR −15% · Ocupación −22%', 'ADR −15% · Occupancy −22%') },
    base:        { label: L('Típico', 'Typical'),           hint: L('Tus datos, sin cambios', 'Your inputs, unchanged') },
    optimistic:  { label: L('Ambicioso', 'Ambitious'),      hint: L('ADR +15% · Ocupación +17%', 'ADR +15% · Occupancy +17%') }
  };

  var SENSITIVITY_ADR_OFFSETS = [-0.2, 0, 0.2];
  var SENSITIVITY_OCC_OFFSETS = [-0.1, 0, 0.1];

  /* ── Boot ────────────────────────────────────────────────── */
  function boot() {
    var sections = document.querySelectorAll('[data-roi-section]');
    sections.forEach(initSection);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* ── Init ────────────────────────────────────────────────── */
  function initSection(sectionEl) {
    if (sectionEl.__nookROIBound) return;
    sectionEl.__nookROIBound = true;

    var ctx = buildContext(sectionEl);
    if (!ctx.unitTypes.length) return;

    ctx.state = defaultStateForMode(ctx.mode, ctx);
    applyURLOverrides(ctx);

    injectExtraFields(ctx); /* site works + marketing / rates / FF&E / length-of-stay inputs (must run before bindOpex) */
    relabelStatic(ctx);     /* cleaning-per-stay, per-unit insurance, occupancy, payback tooltip, scenario names */

    bindModeSwitcher(ctx);
    bindPresets(ctx);
    bindSimple(ctx);
    bindOpex(ctx);
    bindCapex(ctx);
    bindFinancing(ctx);
    bindScenarios(ctx);
    bindSessions(ctx);
    bindEmailDialog(ctx);

    showMode(ctx, ctx.mode);
    renderAll(ctx);
  }

  /* ── Runtime field injection + relabels ──────────────────────
     The operating-cost panel is server-rendered; these add the newer cost
     inputs and correct labels at runtime so the calculator models real
     hospitality economics without a template rebuild. Bilingual via L(). */
  function injectExtraFields(ctx) {
    var op = ctx.state.opex, cap = ctx.state.capex;
    ctx.el.querySelectorAll('.nook-roi__opex-panel .nook-roi__opex-grid').forEach(function (grid, gi) {
      if (grid.__nookExtra) return;
      grid.__nookExtra = true;

      /* Site works & connections (capex, € amount) */
      grid.appendChild(buildOpexField({
        idBase: 'nook-capex-site', suffix: gi,
        label: L('Obra de terreno y conexiones (movimiento de tierras, pilotes, acometidas, accesos)',
                 'Site works & connections (groundworks, piles, utilities, access)'),
        dataAttr: 'data-roi-capex', dataKey: 'siteWorks',
        min: '0', max: '5000000', step: '500', value: String(Math.round((cap.siteWorksAuto ? 0 : (cap.siteWorksCents || 0)) / 100)),
        prefix: currencySymbol(), wide: true,
        note: L('Asume que tus cifras cubren movimiento de tierras y conexiones; introduce tu propio presupuesto cuando lo tengas.',
                'Assumes your entries cover groundworks and connections; enter your own quote when you have one.')
      }));

      /* Marketing / acquisition (%) */
      grid.appendChild(buildOpexField({
        idBase: 'nook-opex-mkt', suffix: gi,
        label: L('Marketing y captación', 'Marketing & acquisition'),
        dataAttr: 'data-roi-opex', dataKey: 'marketingPct',
        min: '0', max: '20', step: '0.5', value: String(op.marketingPct), pct: true
      }));

      /* Business rates (€ per unit / yr) */
      grid.appendChild(buildOpexField({
        idBase: 'nook-opex-rates', suffix: gi,
        label: L('Impuestos de actividad (IBI / tasas)', 'Business rates'),
        dataAttr: 'data-roi-opex', dataKey: 'businessRates',
        min: '0', max: '20000', step: '50', value: String(op.businessRates),
        per: L('/ unidad / año', '/ unit / yr')
      }));

      /* FF&E reserve (%) */
      grid.appendChild(buildOpexField({
        idBase: 'nook-opex-ffe', suffix: gi,
        label: L('Reserva de mobiliario y equipamiento (FF&E)', 'FF&E reserve'),
        dataAttr: 'data-roi-opex', dataKey: 'ffeReservePct',
        min: '0', max: '15', step: '0.5', value: String(op.ffeReservePct), pct: true
      }));

      /* Average length of stay (nights) */
      grid.appendChild(buildOpexField({
        idBase: 'nook-opex-los', suffix: gi,
        label: L('Estancia media', 'Average length of stay'),
        dataAttr: 'data-roi-opex', dataKey: 'avgLengthOfStay',
        min: '1', max: '30', step: '1', value: String(op.avgLengthOfStay),
        per: L('noches', 'nights')
      }));
    });
  }

  function buildOpexField(o) {
    var field = el('div', { class: 'nook-roi__opex-field' + (o.wide ? ' nook-roi__opex-field--wide' : '') });
    var id = o.idBase + '-' + o.suffix;
    field.appendChild(el('label', { class: 'nook-roi__row-label', 'for': id }, o.label));
    var wrap = el('div', { class: 'nook-roi__fin-input-wrap' });
    if (o.prefix) wrap.appendChild(el('span', { class: 'nook-roi__row-currency', 'aria-hidden': 'true' }, o.prefix));
    var attrs = {
      type: 'number', id: id, class: 'nook-roi__row-input',
      min: o.min, max: o.max, step: o.step, value: o.value, inputmode: 'decimal'
    };
    attrs[o.dataAttr] = o.dataKey;
    wrap.appendChild(el('input', attrs));
    if (o.pct) wrap.appendChild(el('span', { class: 'nook-roi__row-pct', 'aria-hidden': 'true' }, '%'));
    else if (o.per) wrap.appendChild(el('span', { class: 'nook-roi__row-pct nook-roi__row-pct--per', 'aria-hidden': 'true' }, o.per));
    field.appendChild(wrap);
    if (o.note) field.appendChild(el('p', { class: 'nook-roi__field-note' }, o.note));
    return field;
  }

  function bindCapex(ctx) {
    ctx.el.querySelectorAll('[data-roi-capex="siteWorks"]').forEach(function (input) {
      input.addEventListener('input', function () {
        var raw = input.value.trim();
        var v = parseFloat(raw);
        /* Empty or non-numeric → treat as €0 of site works (never a stale figure);
           negatives clamped to 0. Once touched, we stop auto-deriving from pod price. */
        if (raw === '' || !isFinite(v) || v < 0) v = 0;
        ctx.state.capex.siteWorksAuto = false; /* operator's own figure wins from now on */
        ctx.state.capex.siteWorksCents = Math.round(v * 100);
        /* Always resync the duplicate input so the two panels never diverge. */
        ctx.el.querySelectorAll('[data-roi-capex="siteWorks"]').forEach(function (inp) {
          if (inp !== input && document.activeElement !== inp && parseFloat(inp.value) !== v) inp.value = String(v);
        });
        renderPortfolio(ctx);
        renderInstitutional(ctx);
        debounceTrack(ctx);
      });
    });
  }

  /* Clamp a numeric value to its <input min/max> attributes (if present). */
  function clampToInputBounds(input, v) {
    var min = parseFloat(input.getAttribute('min'));
    var max = parseFloat(input.getAttribute('max'));
    if (isFinite(min) && v < min) v = min;
    if (isFinite(max) && v > max) v = max;
    return v;
  }

  /* Keep the auto site-works figure in sync with the pod subtotal (until edited). */
  function syncSiteWorksDisplay(ctx, siteWorksCents) {
    if (!ctx.state.capex.siteWorksAuto) return;
    var val = String(Math.round(siteWorksCents / 100));
    ctx.el.querySelectorAll('[data-roi-capex="siteWorks"]').forEach(function (inp) {
      if (document.activeElement !== inp && inp.value !== val) inp.value = val;
    });
  }

  function relabelStatic(ctx) {
    /* C6 — cleaning is per turnover (per stay), with helper */
    ctx.el.querySelectorAll('[data-roi-opex="cleaningPerStay"]').forEach(function (input) {
      var field = input.closest('.nook-roi__opex-field');
      var lbl = field && field.querySelector('.nook-roi__row-label');
      if (lbl) lbl.textContent = L('Limpieza por estancia', 'Cleaning per stay');
      var per = field && field.querySelector('.nook-roi__row-pct--per');
      if (per) per.textContent = L('/ estancia', '/ stay');
      if (field && !field.querySelector('.nook-roi__field-note')) {
        field.appendChild(el('p', { class: 'nook-roi__field-note' },
          L('se cobra por rotación, no por noche', 'charged per turnover, not per night')));
      }
    });
    /* C8 — insurance is per unit per year */
    ctx.el.querySelectorAll('[data-roi-opex="insurance"]').forEach(function (input) {
      var field = input.closest('.nook-roi__opex-field');
      var per = field && field.querySelector('.nook-roi__row-pct--per');
      if (per) per.textContent = L('/ unidad / año', '/ unit / yr');
    });
    /* C14 — occupancy labels annualized (simple-mode label; row labels handled in buildRowEl) */
    ctx.el.querySelectorAll('.nook-roi__panel--simple label[for]').forEach(function (lbl) {
      if (/occ/i.test(lbl.getAttribute('for') || '') && lbl.textContent.indexOf('(') === -1) {
        lbl.textContent = lbl.textContent.trim() + ' ' + L('(anualizada)', '(annualized)');
      }
    });
    /* C4 — occupancy helper caption (calculator guidance, not a marketing stat) */
    ctx.el.querySelectorAll('.nook-roi__panel--simple [data-roi-simple-occ]').forEach(function (input) {
      var field = input.closest('.nook-roi__field');
      if (field && !field.querySelector('.nook-roi__field-note')) {
        field.appendChild(el('p', { class: 'nook-roi__field-note' },
          L('Introduce una hipótesis anual prudente basada en tu propio mercado.',
            'Enter a conservative annualized assumption based on your own market.')));
      }
    });
    /* C13 — payback tooltip reworded */
    ctx.el.querySelectorAll('[data-roi-tooltip]').forEach(function (tip) {
      tip.textContent = L('Meses hasta recuperar tu inversión total mediante el ingreso operativo neto; excluye el servicio de deuda. La obra de terreno usa el valor por defecto mostrado hasta que introduzcas tu propio presupuesto.',
        'Months until your total investment is recovered through net operating income; excludes debt service. Site works use the displayed default until you enter your own quote.');
    });
    /* C9 — scenario names Conservative / Typical / Ambitious */
    ctx.el.querySelectorAll('[data-roi-scenario]').forEach(function (btn) {
      var key = btn.getAttribute('data-roi-scenario');
      var meta = SCENARIO_LABELS[key];
      if (!meta) return;
      var lbl = btn.querySelector('.nook-roi__scenario-label');
      var hint = btn.querySelector('.nook-roi__scenario-hint');
      if (lbl) lbl.textContent = meta.label;
      if (hint) hint.textContent = meta.hint;
    });
  }

  function buildContext(sectionEl) {
    var unitTypes = readUnitTypes(sectionEl);
    var enabledAttr = sectionEl.getAttribute('data-roi-enabled-modes') || 'simple,portfolio,institutional';
    var enabled = enabledAttr.split(',').map(function (s) { return s.trim(); }).filter(Boolean);
    var defaultMode = sectionEl.getAttribute('data-roi-default-mode') || 'simple';
    if (enabled.indexOf(defaultMode) === -1) defaultMode = enabled[0] || 'simple';

    return {
      el: sectionEl,
      sectionId: sectionEl.getAttribute('data-roi-section-id') || '',
      unitTypes: unitTypes,
      enabledModes: enabled,
      mode: defaultMode,
      state: null,
      trackTimer: null /* per-section debounce timer (avoids cross-section interference) */
    };
  }

  function readUnitTypes(sectionEl) {
    var dataEl = sectionEl.querySelector('[data-roi-unit-types]');
    if (!dataEl) return [];
    try {
      var parsed = JSON.parse(dataEl.textContent);
      return parsed.map(function (u, i) {
        return {
          index: i,
          label: String(u.label || ''),
          priceEurCents: parseInt(u.priceEurCents, 10) || 0,
          /* Optional market prices (the dossier's £ / $ list), used instead of
             an FX conversion when the active currency matches. 0 = convert. */
          priceGbpCents: parseInt(u.priceGbpCents, 10) || 0,
          priceUsdCents: parseInt(u.priceUsdCents, 10) || 0
        };
      });
    } catch (e) {
      return [];
    }
  }

  function defaultStateForMode(mode, ctx) {
    var seed = DEFAULT_SEEDS[mode] || DEFAULT_SEEDS.simple;
    return {
      rows: seed.rows.map(function (r) { return normalizeRow(r, ctx); }),
      opex: Object.assign({}, DEFAULT_OPEX),
      capex: Object.assign({}, DEFAULT_CAPEX),
      financing: Object.assign({}, DEFAULT_FIN),
      /* Always attached — a ?mode=sessions URL override rebuilds state, and the
         sessions sub-state must survive that rebuild. */
      sessions: defaultSessionsState(ctx),
      scenario: 'base'
    };
  }

  /* Sessions sub-state: EUR money defaults converted to the active currency,
     capex pre-filled from the section's unit_type blocks + site works. */
  function defaultSessionsState(ctx) {
    var s = Object.assign({}, DEFAULT_SESSIONS);
    s.revenuePerSlot     = convertEURToLocalRate(s.revenuePerSlot);
    s.resetCost          = convertEURToLocalRate(s.resetCost);
    s.energyCost         = convertEURToLocalRate(s.energyCost);
    s.fixedAnnual        = convertEURToLocalRate(s.fixedAnnual);
    s.maintenanceReserve = convertEURToLocalRate(s.maintenanceReserve);
    s.siteAllowance      = convertEURToLocalRate(s.siteAllowance);
    s.installQuote       = convertEURToLocalRate(s.installQuote);
    s.capexCents         = defaultSessionsCapexCents(ctx);
    return s;
  }

  /* The unit price, ex works: the summed unit_type blocks, with NO site-works
     uplift. The nightly-let model's DEFAULT_CAPEX.siteWorksPct (+40%) is
     deliberately NOT applied here: sessions mode carries the rest of the
     investment as two explicit inputs instead, `installQuote` (transport,
     foundations, installation, quoted per address) and `siteAllowance` (site
     services and permissions), so nothing is double-counted or hidden. */
  function defaultSessionsCapexCents(ctx) {
    if (!ctx || !ctx.unitTypes || !ctx.unitTypes.length) return 0;
    var pods = 0;
    for (var i = 0; i < ctx.unitTypes.length; i++) pods += getUnitPriceLocal(ctx, i);
    return pods;
  }

  function normalizeRow(r, ctx) {
    var unit = ctx.unitTypes[r.unitIndex] || ctx.unitTypes[0];
    return {
      unitIndex: unit ? unit.index : 0,
      qty: clampInt(r.qty, 1, 99, 1),
      rate: convertEURToLocalRate(r.rate),
      occ: clampInt(r.occ, 20, 95, 75)
    };
  }

  /* ── Mode switcher ───────────────────────────────────────── */
  function selectMode(ctx, mode) {
    if (mode === ctx.mode) return;
    if (ctx.enabledModes.indexOf(mode) === -1) return;
    ctx.mode = mode;
    if (!ctx.state.rows.length) {
      var seed = DEFAULT_SEEDS[mode] || DEFAULT_SEEDS.simple;
      ctx.state.rows = seed.rows.map(function (r) { return normalizeRow(r, ctx); });
    }
    showMode(ctx, mode);
    renderAll(ctx);
    track('nook_roi_mode_switch', { mode: mode });
  }

  function bindModeSwitcher(ctx) {
    var tabs = Array.prototype.slice.call(ctx.el.querySelectorAll('[data-roi-mode-tab]'));
    tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function () {
        selectMode(ctx, tab.getAttribute('data-roi-mode-tab'));
      });

      /* APG tabs pattern: ONE tab in the page tab order, arrows move between them.
         Without this every tab is a tab stop and Left/Right/Home/End do nothing —
         a keyboard user has to tab through all four to reach the panel. */
      tab.addEventListener('keydown', function (e) {
        var next = -1;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown')     next = (i + 1) % tabs.length;
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp')   next = (i - 1 + tabs.length) % tabs.length;
        else if (e.key === 'Home')                               next = 0;
        else if (e.key === 'End')                                next = tabs.length - 1;
        else return;
        e.preventDefault();
        var target = tabs[next];
        selectMode(ctx, target.getAttribute('data-roi-mode-tab'));
        target.focus();
      });
    });

    /* Reflect the server-rendered default into the roving tabindex on init. */
    updateTabRoving(ctx, ctx.mode);
  }

  function updateTabRoving(ctx, mode) {
    ctx.el.querySelectorAll('[data-roi-mode-tab]').forEach(function (tab) {
      var active = tab.getAttribute('data-roi-mode-tab') === mode;
      tab.setAttribute('tabindex', active ? '0' : '-1');
    });
  }

  function showMode(ctx, mode) {
    ctx.el.querySelectorAll('[data-roi-mode-tab]').forEach(function (tab) {
      var active = tab.getAttribute('data-roi-mode-tab') === mode;
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      tab.classList.toggle('is-active', active);
    });
    updateTabRoving(ctx, mode);
    ctx.el.querySelectorAll('[data-roi-panel]').forEach(function (panel) {
      var visible = panel.getAttribute('data-roi-panel') === mode;
      panel.hidden = !visible;
    });
  }

  /* ── Presets ─────────────────────────────────────────────── */
  function bindPresets(ctx) {
    ctx.el.querySelectorAll('[data-roi-preset]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var key = btn.getAttribute('data-roi-preset');
        applyPreset(ctx, key);
        renderAll(ctx);
        track('nook_roi_preset_select', { preset: key });
      });
    });

    /* Add-row buttons — Portfolio + Institutional each have their own */
    ctx.el.querySelectorAll('[data-roi-add-row]').forEach(function (addBtn) {
      addBtn.addEventListener('click', function () {
        ctx.state.rows.push(normalizeRow({ unitIndex: 0, qty: 1, rate: 250, occ: 75 }, ctx));
        renderAll(ctx);
        track('nook_roi_row_add', { count: ctx.state.rows.length });
      });
    });
  }

  function applyPreset(ctx, key) {
    var preset = PRESETS[key];
    if (!preset) return;
    ctx.state.rows = preset.rows.map(function (r) { return normalizeRow(r, ctx); });
  }

  /* ── Simple mode ─────────────────────────────────────────── */
  function bindSimple(ctx) {
    var panel = panelEl(ctx, 'simple'); if (!panel) return;
    var unit = panel.querySelector('[data-roi-simple-unit]');
    var qty  = panel.querySelector('[data-roi-simple-qty]');
    var rate = panel.querySelector('[data-roi-simple-rate]');
    var occ  = panel.querySelector('[data-roi-simple-occ]');
    if (!unit || !qty || !rate || !occ) return;

    if (CURRENCY !== 'EUR') {
      rate.min = convertEURToLocalRate(parseInt(rate.min, 10));
      rate.max = convertEURToLocalRate(parseInt(rate.max, 10));
      rate.value = convertEURToLocalRate(parseInt(rate.value, 10));
      rate.step = Math.max(5, Math.round(5 * FX_RATE));
    }

    function readState() {
      ctx.state.rows = [{
        unitIndex: parseInt(unit.value, 10) || 0,
        qty: parseInt(qty.value, 10) || 1,
        rate: parseInt(rate.value, 10) || 0,
        occ: parseInt(occ.value, 10) || 50
      }];
    }

    [unit, qty, rate, occ].forEach(function (el) {
      el.addEventListener('input', function () {
        readState();
        renderSimple(ctx);
        debounceTrack(ctx);
      });
    });

    panel.querySelectorAll('[data-roi-simple-chip]').forEach(function (chip) {
      chip.addEventListener('click', function () {
        var v = parseInt(chip.getAttribute('data-roi-simple-chip'), 10);
        if (isNaN(v)) return;
        qty.value = Math.min(v, parseInt(qty.max, 10) || 99);
        readState();
        renderSimple(ctx);
        track('nook_roi_chip_select', { quantity: v });
      });
    });

    var trig = panel.querySelector('[data-roi-tooltip-trigger]');
    var tip  = panel.querySelector('[data-roi-tooltip]');
    if (trig && tip) {
      trig.addEventListener('click', function (e) {
        e.stopPropagation();
        tip.classList.toggle('is-visible');
      });
      document.addEventListener('click', function () { tip.classList.remove('is-visible'); });
    }

    readState();
  }

  /* ── Opex / Financing / Scenarios ────────────────────────── */
  function bindOpex(ctx) {
    ctx.el.querySelectorAll('[data-roi-opex]').forEach(function (input) {
      input.addEventListener('input', function () {
        var key = input.getAttribute('data-roi-opex');
        var v = parseFloat(input.value);
        if (!isFinite(v)) return;
        /* Clamp to the input's own bounds so a typed/pasted out-of-range value
           can't silently inflate NOI/IRR (defaults keep their sane ranges). */
        v = clampToInputBounds(input, v);
        ctx.state.opex[key] = v;
        /* Mirror value to duplicates (Portfolio + Institutional share state) */
        ctx.el.querySelectorAll('[data-roi-opex="' + key + '"]').forEach(function (inp) {
          if (inp !== input && parseFloat(inp.value) !== v) inp.value = v;
        });
        renderPortfolio(ctx);
        renderInstitutional(ctx);
        debounceTrack(ctx);
      });
    });
    ctx.el.querySelectorAll('[data-roi-opex-preset]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var fees = parseFloat(btn.getAttribute('data-roi-opex-preset'));
        if (!isFinite(fees)) return;
        ctx.state.opex.platformFees = fees;
        ctx.el.querySelectorAll('[data-roi-opex="platformFees"]').forEach(function (inp) { inp.value = fees; });
        ctx.el.querySelectorAll('[data-roi-opex-preset]').forEach(function (b) {
          var same = b.getAttribute('data-roi-opex-preset') === btn.getAttribute('data-roi-opex-preset');
          b.classList.toggle('is-active', same);
          b.setAttribute('aria-pressed', same ? 'true' : 'false');
        });
        renderPortfolio(ctx);
        renderInstitutional(ctx);
      });
    });
  }

  function bindFinancing(ctx) {
    ctx.el.querySelectorAll('[data-roi-fin]').forEach(function (input) {
      input.addEventListener('input', function () {
        var key = input.getAttribute('data-roi-fin');
        var v = parseFloat(input.value);
        if (isFinite(v)) ctx.state.financing[key] = v;
        renderInstitutional(ctx);
        debounceTrack(ctx);
      });
    });
  }

  function bindScenarios(ctx) {
    ctx.el.querySelectorAll('[data-roi-scenario]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var s = btn.getAttribute('data-roi-scenario');
        if (!SCENARIO_MULTS[s]) return;
        ctx.state.scenario = s;
        ctx.el.querySelectorAll('[data-roi-scenario]').forEach(function (b) {
          b.classList.toggle('is-active', b === btn);
          b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
        });
        renderInstitutional(ctx);
      });
    });
  }

  /* ── Rows (portfolio + institutional share state; each panel has its own DOM container) ── */
  function delegateRows(ctx) {
    if (ctx.el.__rowsBound) return;
    ctx.el.__rowsBound = true;

    ctx.el.addEventListener('input', function (e) {
      var rowsContainer = e.target.closest('[data-roi-rows]');
      if (!rowsContainer) return;
      var row = e.target.closest('[data-roi-row]');
      if (!row) return;
      syncRowFromDOM(ctx, row);
      renderPortfolio(ctx);
      renderInstitutional(ctx);
      syncRowValues(ctx); /* mirror across duplicate rows containers */
      debounceTrack(ctx);
    });
    ctx.el.addEventListener('change', function (e) {
      var rowsContainer = e.target.closest('[data-roi-rows]');
      if (!rowsContainer) return;
      var row = e.target.closest('[data-roi-row]');
      if (!row) return;
      syncRowFromDOM(ctx, row);
      renderPortfolio(ctx);
      renderInstitutional(ctx);
      syncRowValues(ctx);
    });
    ctx.el.addEventListener('click', function (e) {
      var removeBtn = e.target.closest('[data-roi-row-remove]');
      if (!removeBtn) return;
      var rowsContainer = removeBtn.closest('[data-roi-rows]');
      if (!rowsContainer) return;
      var row = removeBtn.closest('[data-roi-row]');
      if (!row) return;
      var idx = parseInt(row.getAttribute('data-roi-row-index'), 10);
      if (isNaN(idx)) return;
      ctx.state.rows.splice(idx, 1);
      if (!ctx.state.rows.length) {
        ctx.state.rows.push(normalizeRow({ unitIndex: 0, qty: 1, rate: 250, occ: 75 }, ctx));
      }
      renderAll(ctx);
      track('nook_roi_row_remove', { count: ctx.state.rows.length });
    });
  }

  /* Keep inputs across duplicated rows containers (Portfolio + Institutional) in sync.
     Called after a row is edited in one container so the mirror reflects the change. */
  function syncRowValues(ctx) {
    var containers = ctx.el.querySelectorAll('[data-roi-rows]');
    if (containers.length < 2) return;
    ctx.state.rows.forEach(function (row, i) {
      containers.forEach(function (container) {
        var rowEl = container.querySelector('[data-roi-row-index="' + i + '"]');
        if (!rowEl) return;
        var unit = rowEl.querySelector('[data-roi-row-unit]');
        var qty  = rowEl.querySelector('[data-roi-row-qty]');
        var rate = rowEl.querySelector('[data-roi-row-rate]');
        var occ  = rowEl.querySelector('[data-roi-row-occ]');
        if (unit && parseInt(unit.value, 10) !== row.unitIndex) unit.value = String(row.unitIndex);
        if (qty  && parseInt(qty.value, 10) !== row.qty)        qty.value  = String(row.qty);
        if (rate && parseInt(rate.value, 10) !== row.rate)      rate.value = String(row.rate);
        if (occ  && parseInt(occ.value, 10) !== row.occ)        occ.value  = String(row.occ);
      });
    });
  }

  function syncRowFromDOM(ctx, rowEl) {
    var idx = parseInt(rowEl.getAttribute('data-roi-row-index'), 10);
    if (isNaN(idx) || !ctx.state.rows[idx]) return;
    var row = ctx.state.rows[idx];
    var unit = rowEl.querySelector('[data-roi-row-unit]');
    var qty  = rowEl.querySelector('[data-roi-row-qty]');
    var rate = rowEl.querySelector('[data-roi-row-rate]');
    var occ  = rowEl.querySelector('[data-roi-row-occ]');
    if (unit) row.unitIndex = parseInt(unit.value, 10) || 0;
    if (qty)  row.qty  = clampInt(parseInt(qty.value, 10), 1, 99, 1);
    if (rate) row.rate = Math.max(0, parseInt(rate.value, 10) || 0);
    if (occ)  row.occ  = clampInt(parseInt(occ.value, 10), 0, 100, 75);
  }

  /* ── DOM builders (safe — no innerHTML) ──────────────────── */
  function el(tag, attrs, text) {
    var node = document.createElement(tag);
    if (attrs) {
      Object.keys(attrs).forEach(function (k) {
        if (k === 'class')        node.className = attrs[k];
        else if (k === 'dataset') Object.keys(attrs[k]).forEach(function (dk) { node.dataset[dk] = attrs[k][dk]; });
        else if (k === 'html')    return; /* intentionally blocked */
        else                      node.setAttribute(k, attrs[k]);
      });
    }
    if (text != null) node.textContent = String(text);
    return node;
  }
  function appendAll(parent, children) {
    children.forEach(function (c) { if (c) parent.appendChild(c); });
    return parent;
  }

  function buildRowEl(ctx, row, index) {
    var wrap = el('div', { class: 'nook-roi__row', 'data-roi-row': '', 'data-roi-row-index': String(index) });

    /* Unit cell */
    var unitCell = el('div', { class: 'nook-roi__row-cell nook-roi__row-cell--unit' });
    unitCell.appendChild(el('label', { class: 'nook-roi__row-label' }, T.portfolioUnitLabel || 'Unit type'));
    var select = el('select', { class: 'nook-roi__row-select', 'data-roi-row-unit': '', 'aria-label': T.portfolioUnitLabel || 'Unit type' });
    ctx.unitTypes.forEach(function (u) {
      var opt = el('option', { value: String(u.index) }, u.label);
      if (u.index === row.unitIndex) opt.selected = true;
      select.appendChild(opt);
    });
    unitCell.appendChild(select);
    wrap.appendChild(unitCell);

    /* Qty cell */
    var qtyCell = el('div', { class: 'nook-roi__row-cell nook-roi__row-cell--qty' });
    qtyCell.appendChild(el('label', { class: 'nook-roi__row-label' }, T.portfolioQtyLabel || 'Qty'));
    qtyCell.appendChild(el('input', {
      type: 'number', class: 'nook-roi__row-input', 'data-roi-row-qty': '',
      min: '1', max: '99', step: '1', value: String(row.qty),
      inputmode: 'numeric', 'aria-label': T.portfolioQtyLabel || 'Quantity'
    }));
    wrap.appendChild(qtyCell);

    /* Rate cell */
    var rateCell = el('div', { class: 'nook-roi__row-cell nook-roi__row-cell--rate' });
    rateCell.appendChild(el('label', { class: 'nook-roi__row-label' }, T.portfolioRateLabel || 'Rate'));
    var rateWrap = el('div', { class: 'nook-roi__row-rate-wrap' });
    rateWrap.appendChild(el('span', { class: 'nook-roi__row-currency', 'aria-hidden': 'true' }, currencySymbol()));
    rateWrap.appendChild(el('input', {
      type: 'number', class: 'nook-roi__row-input', 'data-roi-row-rate': '',
      min: '0', max: '3000', step: '5', value: String(row.rate),
      inputmode: 'numeric', 'aria-label': T.portfolioRateLabel || 'Rate'
    }));
    rateCell.appendChild(rateWrap);
    wrap.appendChild(rateCell);

    /* Occ cell */
    var occCell = el('div', { class: 'nook-roi__row-cell nook-roi__row-cell--occ' });
    occCell.appendChild(el('label', { class: 'nook-roi__row-label' }, (T.portfolioOccLabel || 'Occ') + ' ' + L('(anualizada)', '(annualized)')));
    var occWrap = el('div', { class: 'nook-roi__row-occ-wrap' });
    occWrap.appendChild(el('input', {
      type: 'number', class: 'nook-roi__row-input', 'data-roi-row-occ': '',
      min: '0', max: '100', step: '1', value: String(row.occ),
      inputmode: 'numeric', 'aria-label': T.portfolioOccLabel || 'Occupancy'
    }));
    occWrap.appendChild(el('span', { class: 'nook-roi__row-pct', 'aria-hidden': 'true' }, '%'));
    occCell.appendChild(occWrap);
    wrap.appendChild(occCell);

    /* Remove button with inline SVG */
    var removeBtn = el('button', {
      type: 'button', class: 'nook-roi__row-remove',
      'data-roi-row-remove': '', 'aria-label': T.removeRow || 'Remove row'
    });
    var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('width', '16'); svg.setAttribute('height', '16');
    svg.setAttribute('viewBox', '0 0 16 16'); svg.setAttribute('fill', 'none');
    svg.setAttribute('aria-hidden', 'true');
    var path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', 'M3 3l10 10M13 3L3 13');
    path.setAttribute('stroke', 'currentColor'); path.setAttribute('stroke-width', '1.5');
    path.setAttribute('stroke-linecap', 'round');
    svg.appendChild(path);
    removeBtn.appendChild(svg);
    wrap.appendChild(removeBtn);

    return wrap;
  }

  function replaceChildren(parent, nodes) {
    while (parent.firstChild) parent.removeChild(parent.firstChild);
    (nodes || []).forEach(function (n) { if (n) parent.appendChild(n); });
  }

  /* ── Rendering dispatcher ────────────────────────────────── */
  function renderAll(ctx) {
    renderRows(ctx);
    if (ctx.mode === 'simple')        renderSimple(ctx);
    if (ctx.mode === 'portfolio')     renderPortfolio(ctx);
    if (ctx.mode === 'institutional') renderInstitutional(ctx);
    if (ctx.mode === 'sessions')      renderSessions(ctx);
  }

  function renderRows(ctx) {
    var rowContainers = ctx.el.querySelectorAll('[data-roi-rows]');
    if (!rowContainers.length) return;
    rowContainers.forEach(function (container) {
      var nodes = ctx.state.rows.map(function (row, i) { return buildRowEl(ctx, row, i); });
      replaceChildren(container, nodes);
    });
    delegateRows(ctx);
  }

  /* ── Simple render ───────────────────────────────────────────
     Simple mode is disabled on hospitality pages, but the math is kept
     honest (C12): it nets out the SAME operating model as Portfolio, includes
     site works in the investment, and reports 3-year net operating income less
     purchase cost — not gross revenue. */
  function renderSimple(ctx) {
    var panel = panelEl(ctx, 'simple'); if (!panel) return;
    var row = ctx.state.rows[0]; if (!row) return;

    var unitPriceCents = getUnitPriceLocal(ctx, row.unitIndex);
    var totals = computePortfolio(ctx, 1, 1); /* single-row state → per-unit economics + site works */
    var grossRevPerUnit = row.qty > 0 ? Math.round(totals.grossRevenueCents / row.qty) : 0;
    var netRevPerUnit = row.qty > 0 ? Math.round(totals.netRevenueCents / row.qty) : 0;
    var totalNet = totals.netRevenueCents;
    var totalInv = totals.totalInvestmentCents;
    var payback = totals.paybackMonths;
    var threeYearNoiLessCost = totals.threeYearProfitCents; /* net × 3 − investment (incl. site works) */

    relabelSimpleOutputs(panel);

    setText(panel, '[data-roi-simple-qty-out]', String(row.qty));
    setText(panel, '[data-roi-simple-rate-out]', formatMoney(row.rate * 100));
    setText(panel, '[data-roi-simple-occ-out]', row.occ + '%');
    setText(panel, '[data-roi-simple-unit-price]', formatMoney(unitPriceCents));
    setText(panel, '[data-roi-result="payback"]', formatPayback(payback));
    setText(panel, '[data-roi-result="revPerUnit"]', formatMoney(netRevPerUnit));
    setText(panel, '[data-roi-result="totalRevenue"]', formatMoney(totals.netRevenueCents));
    setText(panel, '[data-roi-result="totalInvestment"]', formatMoney(totalInv));
    setText(panel, '[data-roi-result="threeYearProfit"]', formatMoney(threeYearNoiLessCost));

    var profitEl = panel.querySelector('[data-roi-result="threeYearProfit"]');
    if (profitEl) {
      var card = profitEl.closest('.nook-roi__result-card');
      if (card) card.classList.toggle('nook-roi__result-card--negative', threeYearNoiLessCost < 0);
    }

    updateRangeFill(panel.querySelector('[data-roi-simple-qty]'));
    updateRangeFill(panel.querySelector('[data-roi-simple-rate]'));
    updateRangeFill(panel.querySelector('[data-roi-simple-occ]'));

    renderFormulaLines(panel.querySelector('[data-roi-formula]'), [
      { label: L('Ingresos brutos por unidad', 'Gross revenue per unit'), eq: formatMoney(row.rate * 100) + ' × 365 × ' + row.occ + '% = ' + formatMoney(grossRevPerUnit) },
      { label: L('Ingreso neto por unidad', 'Net operating income per unit'), eq: formatMoney(grossRevPerUnit) + ' − ' + L('costes operativos', 'operating costs') + ' = ' + formatMoney(netRevPerUnit) },
      { label: T.totalInvestment, eq: L('unidades + obra de terreno', 'units + site works') + ' = ' + formatMoney(totalInv) },
      { label: T.paybackPeriod,   eq: formatMoney(totalInv) + ' ÷ ' + formatMoney(totalNet) + ' × 12 = ' + payback + ' ' + (T.months || 'mo') },
      { label: L('Ingreso operativo neto a 3 años menos coste de compra', '3-year net operating income less purchase cost'), eq: '(' + formatMoney(totalNet) + ' × 3) − ' + formatMoney(totalInv) + ' = ' + formatMoney(threeYearNoiLessCost) }
    ]);
    renderBenchmark(panel);
  }

  /* C12 relabel: "3-Year Net Profit" → net-operating-income-less-purchase-cost. */
  function relabelSimpleOutputs(panel) {
    var profitEl = panel.querySelector('[data-roi-result="threeYearProfit"]');
    if (profitEl) {
      var card = profitEl.closest('.nook-roi__result-card');
      var lbl = card && card.querySelector('.nook-roi__result-label');
      if (lbl && !lbl.__nookRelabeled) {
        lbl.textContent = L('Ingreso operativo neto a 3 años menos coste de compra', '3-year net operating income less purchase cost');
        lbl.__nookRelabeled = true;
      }
    }
    var revEl = panel.querySelector('[data-roi-result="revPerUnit"]');
    if (revEl) {
      var rcard = revEl.closest('.nook-roi__result-card');
      var rlbl = rcard && rcard.querySelector('.nook-roi__result-label');
      if (rlbl && !rlbl.__nookRelabeled) {
        rlbl.textContent = L('Ingreso neto anual por unidad', 'Annual net income per unit');
        rlbl.__nookRelabeled = true;
      }
    }
    var totRevEl = panel.querySelector('[data-roi-result="totalRevenue"]');
    if (totRevEl) {
      var tcard = totRevEl.closest('.nook-roi__result-card');
      var tlbl = tcard && tcard.querySelector('.nook-roi__result-label');
      if (tlbl && !tlbl.__nookRelabeled) {
        tlbl.textContent = L('Ingreso neto anual total', 'Total annual net income');
        tlbl.__nookRelabeled = true;
      }
    }
  }

  function renderFormulaLines(container, lines) {
    if (!container) return;
    var nodes = lines.map(function (l) {
      var line = el('div', { class: 'nook-roi__formula-line' });
      line.appendChild(el('span', { class: 'nook-roi__formula-label' }, l.label));
      var eq = el('span', { class: 'nook-roi__formula-eq' }, l.eq);
      line.appendChild(eq);
      return line;
    });
    replaceChildren(container, nodes);
  }

  /* ── Portfolio render ────────────────────────────────────── */
  function renderPortfolio(ctx) {
    var panel = panelEl(ctx, 'portfolio'); if (!panel) return;
    if (!ctx.state.rows.length) {
      toggleEmpty(panel, true);
      return;
    }
    toggleEmpty(panel, false);

    var totals = computePortfolio(ctx, 1, 1);
    syncSiteWorksDisplay(ctx, totals.siteWorksCents);
    setText(panel, '[data-roi-p="totalInvestment"]', formatMoney(totals.totalInvestmentCents));
    setText(panel, '[data-roi-p="grossRevenue"]',    formatMoney(totals.grossRevenueCents));
    setText(panel, '[data-roi-p="opex"]',            formatMoney(totals.opexCents));
    setText(panel, '[data-roi-p="netRevenue"]',      formatMoney(totals.netRevenueCents));
    setText(panel, '[data-roi-p="goppar"]',          formatMoney(totals.gopparCents));
    setText(panel, '[data-roi-p="revpar"]',          formatMoney(totals.revparCents));
    setText(panel, '[data-roi-p="blendedAdr"]',      formatMoney(totals.blendedAdrCents));
    setText(panel, '[data-roi-p="payback"]',         formatPayback(totals.paybackMonths));
    setText(panel, '[data-roi-p="netYield"]',        formatPercent(totals.netYieldY1));
    setText(panel, '[data-roi-p="threeYearProfit"]', formatMoney(totals.threeYearProfitCents));
    setText(panel, '[data-roi-p="breakEvenOcc"]',    formatPercent(totals.breakEvenOcc));

    var profitEl = panel.querySelector('[data-roi-p="threeYearProfit"]');
    if (profitEl) {
      var card = profitEl.closest('.nook-roi__metric-card');
      if (card) card.classList.toggle('nook-roi__metric-card--negative', totals.threeYearProfitCents < 0);
    }

    renderPortfolioTable(panel.querySelector('[data-roi-p-table]'), totals);
    renderBenchmark(panel);
  }

  function renderPortfolioTable(tbody, totals) {
    if (!tbody) return;
    var rows = totals.perRow.map(function (r) {
      var tr = el('tr');
      tr.appendChild(el('td', { class: 'nook-roi__t-unit' }, r.unitLabel));
      tr.appendChild(el('td', { class: 'nook-roi__t-num' }, String(r.qty)));
      tr.appendChild(el('td', { class: 'nook-roi__t-num' }, formatMoney(r.rateLocalCents)));
      tr.appendChild(el('td', { class: 'nook-roi__t-num' }, r.occ + '%'));
      tr.appendChild(el('td', { class: 'nook-roi__t-num' }, formatMoney(r.grossRevenueCents)));
      tr.appendChild(el('td', { class: 'nook-roi__t-num' }, formatMoney(r.netRevenueCents)));
      return tr;
    });

    var footer = el('tr', { class: 'nook-roi__t-totals' });
    footer.appendChild(el('td', {}, T.tableTotals || 'Totals'));
    footer.appendChild(el('td', { class: 'nook-roi__t-num' }, String(totals.totalQty)));
    footer.appendChild(el('td', {}));
    footer.appendChild(el('td', {}));
    var grossTd = el('td', { class: 'nook-roi__t-num' });
    grossTd.appendChild(el('strong', {}, formatMoney(totals.grossRevenueCents)));
    footer.appendChild(grossTd);
    var netTd = el('td', { class: 'nook-roi__t-num' });
    netTd.appendChild(el('strong', {}, formatMoney(totals.netRevenueCents)));
    footer.appendChild(netTd);

    rows.push(footer);
    replaceChildren(tbody, rows);
  }

  /* ── Institutional render ────────────────────────────────── */
  function renderInstitutional(ctx) {
    var panel = panelEl(ctx, 'institutional'); if (!panel) return;
    if (!ctx.state.rows.length) {
      toggleEmpty(panel, true);
      return;
    }
    toggleEmpty(panel, false);

    var fin = ctx.state.financing;
    var scenarioMult = SCENARIO_MULTS[ctx.state.scenario] || SCENARIO_MULTS.base;
    var totals = computePortfolio(ctx, scenarioMult.adr, scenarioMult.occ);
    if (!totals.totalInvestmentCents) return;
    syncSiteWorksDisplay(ctx, computePortfolio(ctx, 1, 1).siteWorksCents);

    var cashflowTable = buildCashflow(totals, fin, HOLD_YEARS_CASHFLOW_TABLE);
    var cashflowsForIRR = buildIRRCashflows(totals, fin);

    var irrVal = irrNewton(cashflowsForIRR, 0.1);
    var npvVal = npv(cashflowsForIRR, fin.discountRate / 100);
    var capRate = totals.totalInvestmentCents > 0 ? (cashflowTable[0].noi / totals.totalInvestmentCents) : 0;
    var exitIdx = Math.min(fin.holdYears, cashflowTable.length) - 1;
    var exitYear = cashflowTable[exitIdx];
    var exitValCents = exitYear ? exitYear.noi * fin.exitMultiple : 0;

    setText(panel, '[data-roi-i="totalInvestment"]', formatMoney(totals.totalInvestmentCents));
    setText(panel, '[data-roi-i="grossRevenue"]',    formatMoney(totals.grossRevenueCents));
    setText(panel, '[data-roi-i="netRevenue"]',      formatMoney(totals.netRevenueCents));
    setText(panel, '[data-roi-i="goppar"]',          formatMoney(totals.gopparCents));
    setText(panel, '[data-roi-i="revpar"]',          formatMoney(totals.revparCents));
    setText(panel, '[data-roi-i="blendedAdr"]',      formatMoney(totals.blendedAdrCents));
    setText(panel, '[data-roi-i="irr"]',             isFinite(irrVal) ? formatPercent(irrVal) : '—');
    setText(panel, '[data-roi-i="npv"]',             formatMoney(npvVal));
    setText(panel, '[data-roi-i="capRate"]',         formatPercent(capRate));
    setText(panel, '[data-roi-i="exitValuation"]',   formatMoney(exitValCents));
    setText(panel, '[data-roi-i="exitMultiple"]',    fin.exitMultiple + '×');

    renderCashflowTable(panel.querySelector('[data-roi-cashflow]'), cashflowTable);
    renderSensitivity(panel.querySelector('[data-roi-sensitivity]'), ctx, fin);
    renderPortfolioTable(panel.querySelector('[data-roi-i-table]'), totals);
    renderFormulaLines(panel.querySelector('[data-roi-i-formula]'), [
      { label: L('Inversión total', 'Total investment'), eq: L('unidades', 'units') + ' ' + formatMoney(totals.podSubtotalCents) + ' + ' + L('obra de terreno y conexiones', 'site works & connections') + ' ' + formatMoney(totals.siteWorksCents) + ' = ' + formatMoney(totals.totalInvestmentCents) },
      { label: T.metricCapRate,        eq: formatPercent(capRate) + ' = NOI Y1 ' + formatMoney(cashflowTable[0].noi) + ' ÷ ' + formatMoney(totals.totalInvestmentCents) },
      { label: T.metricNpv,            eq: formatMoney(npvVal) + ' (' + L('descontado al', 'discounted at') + ' ' + fin.discountRate + '%)' },
      { label: T.metricIrr,            eq: (isFinite(irrVal) ? formatPercent(irrVal) : '—') + ' · Y0 ' + L('capital', 'equity') + ' + Y1…Y' + fin.holdYears + ' FCF + ' + L('valor terminal', 'terminal') + ' @ ' + fin.exitMultiple + '× NOI' },
      { label: T.metricExitValuation,  eq: formatMoney(exitValCents) + ' = NOI Y' + fin.holdYears + ' × ' + fin.exitMultiple + '×' },
      { label: L('Escaladores anuales', 'Annual escalators'), eq: L('Ingresos', 'Revenue') + ' +' + Math.round(REVENUE_ESCALATOR * 100) + '% / ' + L('año', 'yr') + ' · Opex +' + Math.round(OPEX_ESCALATOR * 100) + '% / ' + L('año', 'yr') + L(' (opex nunca por debajo de ingresos)', ' (opex never below revenue)') }
    ]);
  }

  function renderCashflowTable(tbody, rows) {
    if (!tbody) return;
    var nodes = rows.map(function (r) {
      var tr = el('tr');
      tr.appendChild(el('td', { class: 'nook-roi__t-num' }, String(r.year)));
      tr.appendChild(el('td', { class: 'nook-roi__t-num' }, formatMoney(r.revenue)));
      tr.appendChild(el('td', { class: 'nook-roi__t-num' }, formatMoney(r.opex)));
      tr.appendChild(el('td', { class: 'nook-roi__t-num' }, formatMoney(r.noi)));
      tr.appendChild(el('td', { class: 'nook-roi__t-num' }, r.debt ? formatMoney(r.debt) : '—'));
      var fcfTd = el('td', { class: 'nook-roi__t-num' });
      fcfTd.appendChild(el('strong', {}, formatMoney(r.fcf)));
      tr.appendChild(fcfTd);
      return tr;
    });
    replaceChildren(tbody, nodes);
  }

  function renderSensitivity(grid, ctx, fin) {
    if (!grid) return;
    var results = [];
    var baseIRR = null;
    SENSITIVITY_OCC_OFFSETS.forEach(function (occOff) {
      var row = [];
      SENSITIVITY_ADR_OFFSETS.forEach(function (adrOff) {
        var t = computePortfolio(ctx, 1 + adrOff, 1 + occOff);
        var cfs = buildIRRCashflows(t, fin);
        var r = irrNewton(cfs, 0.1);
        row.push(r);
        if (adrOff === 0 && occOff === 0) baseIRR = r;
      });
      results.push(row);
    });
    var anchor = (baseIRR != null && isFinite(baseIRR)) ? baseIRR : 0.1;

    var nodes = [];
    nodes.push(el('div', { class: 'nook-roi__s-cell nook-roi__s-cell--corner', 'aria-hidden': 'true' }));
    SENSITIVITY_ADR_OFFSETS.forEach(function (off) {
      var label = 'ADR ' + (off > 0 ? '+' : '') + Math.round(off * 100) + '%';
      nodes.push(el('div', { class: 'nook-roi__s-cell nook-roi__s-cell--head' }, label));
    });

    results.forEach(function (row, iOcc) {
      var occOff = SENSITIVITY_OCC_OFFSETS[iOcc];
      var headLabel = 'Occ ' + (occOff > 0 ? '+' : '') + Math.round(occOff * 100) + '%';
      nodes.push(el('div', { class: 'nook-roi__s-cell nook-roi__s-cell--head' }, headLabel));
      row.forEach(function (r, j) {
        var adrOff = SENSITIVITY_ADR_OFFSETS[j];
        var isBase = (adrOff === 0 && occOff === 0);
        var delta = isFinite(r) && isFinite(anchor) ? r - anchor : 0;
        var cls = 'nook-roi__s-cell';
        if (isBase) cls += ' nook-roi__s-cell--base';
        else if (delta > 0.01) cls += ' nook-roi__s-cell--up';
        else if (delta < -0.01) cls += ' nook-roi__s-cell--down';
        var label = isFinite(r) ? formatPercent(r) : '—';
        if (isBase) label += ' · ' + (T.sensitivityBase || 'Base');
        nodes.push(el('div', { class: cls }, label));
      });
    });
    replaceChildren(grid, nodes);
  }

  /* ──────────────────────────────────────────────────────────
     Sessions mode — bind + compute + render
     ────────────────────────────────────────────────────────── */
  function bindSessions(ctx) {
    var panel = panelEl(ctx, 'sessions'); if (!panel) return;

    primeSessionsInputs(ctx, panel);
    collapseSessionGroupsOnMobile(panel);

    panel.querySelectorAll('[data-roi-sess]').forEach(function (input) {
      input.addEventListener('input', function () {
        writeSessionField(ctx, input);
        renderSessions(ctx);
        debounceTrack(ctx);
      });
      /* Commit pass (fires on blur / Enter / stepper). Writes the value the
         maths actually used back into the box, so the displayed figure and the
         emailed figure can never disagree — including after a clamp. Opening
         the email dialog blurs the field, so this always runs first. */
      input.addEventListener('change', function () {
        writeSessionField(ctx, input);
        reconcileSessionInput(ctx, input);
        renderSessions(ctx);
      });
    });
  }

  /* Reads one sessions field into state. A blank or non-numeric box means ZERO,
     never a stale figure — 0 is meaningful for every one of these inputs, and
     computeSessions() already renders the em-dash placeholder when contribution
     or operating weeks collapse to zero. (The site-works handler has always
     treated blank as zero; this makes the sessions fields consistent with it.) */
  function writeSessionField(ctx, input) {
    var key = input.getAttribute('data-roi-sess');
    var raw = input.value.trim();
    var v = parseFloat(raw);
    if (raw === '' || !isFinite(v) || v < 0) v = 0;
    else v = clampToInputBounds(input, v);
    if (key === 'capex') {
      /* Mirrors the siteWorks pattern: once the operator types their own
         figure we stop deriving it from the unit_type blocks. */
      ctx.state.sessions.capexUserEdited = true;
      ctx.state.sessions.capexCents = Math.round(v * 100);
    } else {
      ctx.state.sessions[key] = v;
    }
  }

  function reconcileSessionInput(ctx, input) {
    var key = input.getAttribute('data-roi-sess');
    var s = ctx.state.sessions;
    var v = (key === 'capex') ? Math.round((s.capexCents || 0) / 100) : s[key];
    if (v == null || !isFinite(v)) return;
    if (input.value !== String(v)) input.value = String(v);
  }

  /* Write the FX-converted state values into the server-rendered inputs. */
  function primeSessionsInputs(ctx, panel) {
    var s = ctx.state.sessions; if (!s) return;
    panel.querySelectorAll('[data-roi-sess]').forEach(function (input) {
      var key = input.getAttribute('data-roi-sess');
      var v = (key === 'capex') ? Math.round((s.capexCents || 0) / 100) : s[key];
      if (v == null || !isFinite(v)) return;
      input.value = String(v);
    });
  }

  /* Groups ship open (usable with JS off); on narrow screens only the first
     stays open so the compact live result is reachable without a long scroll. */
  function collapseSessionGroupsOnMobile(panel) {
    var narrow = false;
    try { narrow = window.matchMedia('(max-width: 767px)').matches; } catch (e) { narrow = false; }
    if (!narrow) return;
    var groups = panel.querySelectorAll('[data-roi-sess-group]');
    groups.forEach(function (g, i) { if (i > 0) g.removeAttribute('open'); });
  }

  function syncSessionsCapexInput(ctx, panel) {
    var s = ctx.state.sessions;
    if (!s || s.capexUserEdited) return;
    var val = String(Math.round((s.capexCents || 0) / 100));
    panel.querySelectorAll('[data-roi-sess="capex"]').forEach(function (inp) {
      if (document.activeElement !== inp && inp.value !== val) inp.value = val;
    });
  }

  /* Pure — no DOM. All money in cents. Every division guarded. */
  function computeSessions(ctx) {
    var s = (ctx.state && ctx.state.sessions) || DEFAULT_SESSIONS;

    var slotsPerDay    = Math.max(0, s.slotsPerDay || 0);
    var daysPerWeek    = Math.max(0, s.daysPerWeek || 0);
    var operatingWeeks = Math.max(0, s.operatingWeeks || 0);
    var utilRatio      = clampRatio((s.utilisation || 0) / 100);
    var feeRatio       = clampRatio((s.bookingFeePct || 0) / 100);

    var revenuePerSlotCents = Math.round((s.revenuePerSlot || 0) * 100);
    var resetCostCents      = Math.round((s.resetCost || 0) * 100);
    var energyCostCents     = Math.round((s.energyCost || 0) * 100);
    var fixedAnnualCents    = Math.round((s.fixedAnnual || 0) * 100);
    var maintenanceCents    = Math.round((s.maintenanceReserve || 0) * 100);

    /* Total investment = the unit price (ex works) + the quoted transport,
       foundations and installation + the operator's site services and
       permissions. Both payback and the 7-year threshold divide by this sum,
       never by the unit price alone. */
    var capexCents          = Math.max(0, Math.round(s.capexCents || 0));
    var installQuoteCents   = Math.max(0, Math.round((s.installQuote || 0) * 100));
    var siteAllowanceCents  = Math.max(0, Math.round((s.siteAllowance || 0) * 100));
    var totalInvestmentCents = capexCents + installQuoteCents + siteAllowanceCents;

    var bookedPerWeek = slotsPerDay * daysPerWeek * utilRatio;
    var annualBooked  = bookedPerWeek * operatingWeeks;

    var grossAnnualCents    = annualBooked * revenuePerSlotCents;
    var variableAnnualCents = (annualBooked * (resetCostCents + energyCostCents)) + (grossAnnualCents * feeRatio);
    var fixedTotalCents     = fixedAnnualCents + maintenanceCents;
    var netAnnualCents      = grossAnnualCents - variableAnnualCents - fixedTotalCents;

    /* Contribution per booked slot: what one more booking adds before fixed costs. */
    var contributionCents = (revenuePerSlotCents * (1 - feeRatio)) - resetCostCents - energyCostCents;

    var paybackMonths = netAnnualCents > 0 ? Math.ceil((totalInvestmentCents / netAnnualCents) * 12) : 0;

    /* Thresholds are undefined when a booking does not cover its own turnaround,
       or when the operator runs zero weeks. null renders as the em-dash. */
    var breakEvenSlotsPerWeek = null;
    var slotsPerWeekForSevenYears = null;
    if (contributionCents > 0 && operatingWeeks > 0) {
      breakEvenSlotsPerWeek = fixedTotalCents / operatingWeeks / contributionCents;
      slotsPerWeekForSevenYears =
        ((totalInvestmentCents / SESSIONS_TARGET_PAYBACK_YEARS) + fixedTotalCents) / operatingWeeks / contributionCents;
    }

    return {
      bookedPerWeek: bookedPerWeek,
      annualBooked: annualBooked,
      grossAnnualCents: grossAnnualCents,
      grossMonthlyCents: grossAnnualCents / 12,
      variableAnnualCents: variableAnnualCents,
      fixedAnnualCents: fixedAnnualCents,
      maintenanceCents: maintenanceCents,
      fixedTotalCents: fixedTotalCents,
      netAnnualCents: netAnnualCents,
      contributionCents: contributionCents,
      capexCents: capexCents,
      installQuoteCents: installQuoteCents,
      siteAllowanceCents: siteAllowanceCents,
      totalInvestmentCents: totalInvestmentCents,
      paybackMonths: paybackMonths,
      breakEvenSlotsPerWeek: breakEvenSlotsPerWeek,
      slotsPerWeekForSevenYears: slotsPerWeekForSevenYears,
      revenuePerSlotCents: revenuePerSlotCents,
      resetCostCents: resetCostCents,
      energyCostCents: energyCostCents,
      feeRatio: feeRatio,
      utilRatio: utilRatio,
      slotsPerDay: slotsPerDay,
      daysPerWeek: daysPerWeek,
      operatingWeeks: operatingWeeks
    };
  }

  function renderSessions(ctx) {
    var panel = panelEl(ctx, 'sessions'); if (!panel) return;
    var r = computeSessions(ctx);
    syncSessionsCapexInput(ctx, panel);

    /* Order is deliberate: thresholds first, the visitor's own payback fifth. */
    setText(panel, '[data-roi-s="breakEvenSlots"]',   formatSlotsUp(r.breakEvenSlotsPerWeek));
    setText(panel, '[data-roi-s="contribution"]',     formatMoney(r.contributionCents));
    setText(panel, '[data-roi-s="netAnnual"]',        formatMoney(r.netAnnualCents));
    /* Codex W2 HIGH: with no transport/foundations/installation quote the total
       investment is incomplete, so every figure that divides by it (payback, the
       7-year threshold) carries a "*" and a visible note until the quote is typed. */
    var incomplete = r.installQuoteCents === 0;
    var mark = incomplete ? ' *' : '';
    var noteIds = [];
    Array.prototype.forEach.call(panel.querySelectorAll('[data-roi-s-incomplete]'), function (el) {
      el.hidden = !incomplete;
      if (el.id) noteIds.push(el.id);
    });
    /* The caveat describes every figure that divides by the investment, but only
       while it applies (a hidden element still feeds aria-describedby). */
    ['targetSlots', 'payback', 'paybackCompact', 'totalInvestment'].forEach(function (key) {
      var el = panel.querySelector('[data-roi-s="' + key + '"]');
      if (!el) return;
      if (incomplete && noteIds.length) el.setAttribute('aria-describedby', noteIds.join(' '));
      else el.removeAttribute('aria-describedby');
    });
    setText(panel, '[data-roi-s="targetSlots"]',      formatSlotsUp(r.slotsPerWeekForSevenYears) + mark);
    setText(panel, '[data-roi-s="payback"]',          formatPayback(r.paybackMonths) + mark);
    setText(panel, '[data-roi-s="totalInvestment"]',  formatMoney(r.totalInvestmentCents) + mark);
    setText(panel, '[data-roi-s="buildings"]',        formatMoney(r.capexCents));
    setText(panel, '[data-roi-s="installQuote"]',     formatMoney(r.installQuoteCents));
    setText(panel, '[data-roi-s="siteAllowance"]',    formatMoney(r.siteAllowanceCents));
    setText(panel, '[data-roi-s="bookedWeek"]',       formatSlots(r.bookedPerWeek));
    setText(panel, '[data-roi-s="grossMonthly"]',     formatMoney(r.grossMonthlyCents));
    setText(panel, '[data-roi-s="grossAnnual"]',      formatMoney(r.grossAnnualCents));
    setText(panel, '[data-roi-s="netAnnualCompact"]', formatMoney(r.netAnnualCents));
    setText(panel, '[data-roi-s="paybackCompact"]',   formatPayback(r.paybackMonths) + mark);

    var netEl = panel.querySelector('[data-roi-s="netAnnual"]');
    if (netEl) {
      var card = netEl.closest('.nook-roi__metric-card');
      if (card) card.classList.toggle('nook-roi__metric-card--negative', r.netAnnualCents < 0);
    }
    var live = panel.querySelector('[data-roi-sess-live]');
    if (live) live.classList.toggle('is-negative', r.netAnnualCents < 0);

    var pct = formatPercent(r.utilRatio);
    var feePct = formatPercent(r.feeRatio);
    renderFormulaLines(panel.querySelector('[data-roi-s-formula]'), [
      {
        label: T.sessionsFormulaGross || 'Gross revenue',
        eq: r.slotsPerDay + ' × ' + r.daysPerWeek + ' × ' + pct + ' × ' + r.operatingWeeks +
            ' = ' + formatSlots(r.annualBooked) + ' × ' + formatMoney(r.revenuePerSlotCents) +
            ' = ' + formatMoney(r.grossAnnualCents)
      },
      {
        label: T.sessionsFormulaVariable || 'Variable costs',
        eq: formatSlots(r.annualBooked) + ' × (' + formatMoney(r.resetCostCents) + ' + ' + formatMoney(r.energyCostCents) + ')' +
            ' + ' + formatMoney(r.grossAnnualCents) + ' × ' + feePct +
            ' = ' + formatMoney(r.variableAnnualCents)
      },
      {
        label: T.sessionsFormulaFixed || 'Fixed costs',
        eq: formatMoney(r.fixedAnnualCents) + ' + ' + formatMoney(r.maintenanceCents) + ' = ' + formatMoney(r.fixedTotalCents)
      },
      {
        label: T.sessionsFormulaNet || 'Annual net income',
        eq: formatMoney(r.grossAnnualCents) + ' − ' + formatMoney(r.variableAnnualCents) + ' − ' +
            formatMoney(r.fixedTotalCents) + ' = ' + formatMoney(r.netAnnualCents)
      }
    ]);
  }

  /* ──────────────────────────────────────────────────────────
     Math: core portfolio calc
     ────────────────────────────────────────────────────────── */
  function computePortfolio(ctx, adrMult, occMult) {
    adrMult = (typeof adrMult === 'number') ? adrMult : 1;
    occMult = (typeof occMult === 'number') ? occMult : 1;
    var opex = ctx.state.opex;
    /* Variable opex as a share of gross: utilities + platform + management +
       marketing + maintenance reserve + FF&E reserve. */
    var variablePct = (opex.utilities + opex.platformFees + opex.management +
      opex.marketingPct + opex.maintenance + opex.ffeReservePct) / 100;
    var los = Math.max(1, opex.avgLengthOfStay || 3); /* nights per stay */

    var podSubtotalCents = 0;
    var grossRevenueCents = 0;
    var cleaningOpexCents = 0;
    var totalQty = 0;
    var totalUnitNights = 0;
    var totalBookedNights = 0;
    var perRowRaw = [];

    /* Per-unit fixed annual costs (business rates + insurance) — charged per unit,
       not once for the whole portfolio (C3/C8). */
    var fixedPerUnitCents = Math.round((opex.businessRates + opex.insurance) * 100);

    /* First pass: per-row gross + cleaning + qty. Cleaning is charged per turnover
       (per stay), so cleaning events = booked nights ÷ average length of stay (C6). */
    ctx.state.rows.forEach(function (row) {
      var unit = ctx.unitTypes[row.unitIndex] || ctx.unitTypes[0];
      var unitPriceCents = getUnitPriceLocal(ctx, row.unitIndex);
      var rateLocal = row.rate * adrMult;
      var rateLocalCents = Math.round(rateLocal * 100);
      var effectiveOccRatio = clampRatio((row.occ / 100) * occMult);
      var unitAnnualRevCents = Math.round(rateLocal * 365 * effectiveOccRatio * 100);
      var rowGross = unitAnnualRevCents * row.qty;
      var bookedNights = Math.round(row.qty * 365 * effectiveOccRatio);
      var cleaningStays = bookedNights / los;
      var cleaningRow = Math.round(cleaningStays * opex.cleaningPerStay * 100);
      var rowOpexPct = rowGross * variablePct;
      var rowFixed = row.qty * fixedPerUnitCents;

      podSubtotalCents += unitPriceCents * row.qty;
      grossRevenueCents += rowGross;
      cleaningOpexCents += cleaningRow;
      totalQty += row.qty;
      totalUnitNights += row.qty * 365;
      totalBookedNights += bookedNights;

      perRowRaw.push({
        unit: unit, row: row,
        unitPriceCents: unitPriceCents, rateLocalCents: rateLocalCents,
        effectiveOccRatio: effectiveOccRatio, unitAnnualRevCents: unitAnnualRevCents,
        rowGross: rowGross, rowOpexPct: rowOpexPct, cleaningRow: cleaningRow, rowFixed: rowFixed
      });
    });

    /* Site works & connections (groundworks, piles, utilities, access) — part of
       total investment (C2). Auto = a % of pod subtotal; else the operator's € figure. */
    var capex = ctx.state.capex || DEFAULT_CAPEX;
    var siteWorksCents = capex.siteWorksAuto
      ? Math.round(podSubtotalCents * ((capex.siteWorksPct || 0) / 100))
      : Math.max(0, Math.round(capex.siteWorksCents || 0));
    var totalInvestmentCents = podSubtotalCents + siteWorksCents;

    /* Second pass: per-row net (fixed costs are per-unit, so row sums = headline). */
    var perRow = perRowRaw.map(function (r) {
      var rowOpex = r.rowOpexPct + r.cleaningRow + r.rowFixed;
      return {
        unitLabel: r.unit ? r.unit.label : '—',
        unitIndex: r.row.unitIndex,
        qty: r.row.qty,
        rateLocalCents: r.rateLocalCents,
        occ: Math.round(r.effectiveOccRatio * 100),
        unitAnnualRevCents: r.unitAnnualRevCents,
        grossRevenueCents: r.rowGross,
        opexCents: rowOpex,
        netRevenueCents: r.rowGross - rowOpex
      };
    });

    var fixedAnnualCents = totalQty * fixedPerUnitCents;
    var opexPctCents = grossRevenueCents * variablePct;
    var opexCents = opexPctCents + cleaningOpexCents + fixedAnnualCents;
    var netRevenueCents = grossRevenueCents - opexCents;
    var gopparCents = totalUnitNights > 0 ? Math.round(netRevenueCents / totalUnitNights) : 0;
    var revparCents = totalUnitNights > 0 ? Math.round(grossRevenueCents / totalUnitNights) : 0;
    var blendedAdrCents = totalBookedNights > 0 ? Math.round(grossRevenueCents / totalBookedNights) : 0;
    var netYieldY1 = totalInvestmentCents > 0 ? netRevenueCents / totalInvestmentCents : 0;
    var paybackMonths = netRevenueCents > 0 ? Math.ceil((totalInvestmentCents / netRevenueCents) * 12) : 0;
    var threeYearProfitCents = (netRevenueCents * 3) - totalInvestmentCents;

    /* Break-even occupancy (C5): ALL fixed annual costs ÷ (annual room-nights ×
       per-night contribution margin). Contribution per night = ADR × (1 − variablePct)
       − cleaning cost per night, where cleaning per night = cleaningPerStay ÷ LOS.
       (Debt service is added by the caller in financed/institutional views; the
       unlevered portfolio break-even below uses insurance + business rates.) */
    var breakEvenOcc = 0;
    if (totalQty > 0) {
      /* Inventory-weighted ADR so break-even is defined even at 0% occupancy
         (blended ADR collapses to 0 when no nights are booked). */
      var mixAdrCents = 0;
      perRowRaw.forEach(function (r) { mixAdrCents += r.rateLocalCents * r.row.qty; });
      mixAdrCents = mixAdrCents / totalQty;
      var blendedAdrLocal = (totalBookedNights > 0 ? blendedAdrCents : mixAdrCents) / 100;
      var annualNights = totalQty * 365;
      var cleaningPerNight = opex.cleaningPerStay / los;
      var contributionPerNight = (blendedAdrLocal * (1 - variablePct)) - cleaningPerNight;
      var fixedAnnualLocal = fixedAnnualCents / 100;
      if (contributionPerNight > 0 && annualNights > 0) {
        breakEvenOcc = clampRatio(fixedAnnualLocal / (annualNights * contributionPerNight));
      } else if (contributionPerNight <= 0) {
        /* Contribution margin <= 0: never breaks even at any occupancy. */
        breakEvenOcc = 1;
      }
    }

    return {
      totalInvestmentCents: totalInvestmentCents,
      podSubtotalCents: podSubtotalCents,
      siteWorksCents: siteWorksCents,
      grossRevenueCents: grossRevenueCents,
      opexCents: opexCents,
      fixedAnnualCents: fixedAnnualCents,
      netRevenueCents: netRevenueCents,
      gopparCents: gopparCents,
      revparCents: revparCents,
      blendedAdrCents: blendedAdrCents,
      netYieldY1: netYieldY1,
      paybackMonths: paybackMonths,
      threeYearProfitCents: threeYearProfitCents,
      breakEvenOcc: breakEvenOcc,
      totalQty: totalQty,
      perRow: perRow
    };
  }

  function buildCashflow(totals, fin, years) {
    var rows = [];
    var debtPrincipalCents = Math.round(totals.totalInvestmentCents * (fin.ltv / 100));
    var annualDebt = annualDebtService(debtPrincipalCents, fin.interestRate / 100, fin.amortYears);
    for (var y = 1; y <= years; y++) {
      var revenue = totals.grossRevenueCents * Math.pow(1 + REVENUE_ESCALATOR, y - 1);
      var opex = totals.opexCents * Math.pow(1 + OPEX_ESCALATOR, y - 1);
      var noi = revenue - opex;
      var debt = y <= fin.amortYears ? annualDebt : 0;
      var fcf = noi - debt;
      rows.push({ year: y, revenue: revenue, opex: opex, noi: noi, debt: debt, fcf: fcf });
    }
    return rows;
  }

  function buildIRRCashflows(totals, fin) {
    var debtPrincipalCents = Math.round(totals.totalInvestmentCents * (fin.ltv / 100));
    var equityCents = totals.totalInvestmentCents - debtPrincipalCents;
    var annualDebt = annualDebtService(debtPrincipalCents, fin.interestRate / 100, fin.amortYears);
    var cashflows = [-equityCents];
    var lastNOI = 0;
    for (var y = 1; y <= fin.holdYears; y++) {
      var revenue = totals.grossRevenueCents * Math.pow(1 + REVENUE_ESCALATOR, y - 1);
      var opex = totals.opexCents * Math.pow(1 + OPEX_ESCALATOR, y - 1);
      var noi = revenue - opex;
      var debt = y <= fin.amortYears ? annualDebt : 0;
      cashflows.push(noi - debt);
      lastNOI = noi;
    }
    var terminal = lastNOI * fin.exitMultiple;
    var remainingDebt = remainingDebtBalance(debtPrincipalCents, fin.interestRate / 100, fin.amortYears, fin.holdYears);
    terminal -= remainingDebt;
    cashflows[cashflows.length - 1] += terminal;
    return cashflows;
  }

  function annualDebtService(principalCents, annualRate, years) {
    if (principalCents <= 0 || years <= 0) return 0;
    if (annualRate === 0) return principalCents / years;
    var r = annualRate;
    var n = years;
    return principalCents * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
  }

  function remainingDebtBalance(principalCents, annualRate, years, afterYears) {
    if (principalCents <= 0 || afterYears >= years) return 0;
    if (annualRate === 0) return principalCents * (1 - afterYears / years);
    var r = annualRate;
    var full = Math.pow(1 + r, years);
    var partial = Math.pow(1 + r, afterYears);
    return principalCents * (full - partial) / (full - 1);
  }

  function irrNewton(cashflows, guess) {
    if (!cashflows || cashflows.length < 2) return NaN;
    var hasPos = false, hasNeg = false;
    for (var i = 0; i < cashflows.length; i++) {
      if (cashflows[i] > 0) hasPos = true;
      if (cashflows[i] < 0) hasNeg = true;
    }
    if (!hasPos || !hasNeg) return NaN;

    var r = guess || 0.1;
    var maxIter = 200;
    var tolerance = 1e-7;
    for (var iter = 0; iter < maxIter; iter++) {
      var npvR = 0, dnpvR = 0;
      for (var t = 0; t < cashflows.length; t++) {
        var base = Math.pow(1 + r, t);
        npvR += cashflows[t] / base;
        if (t > 0) dnpvR -= t * cashflows[t] / (base * (1 + r));
      }
      if (Math.abs(dnpvR) < 1e-12) break;
      var newR = r - npvR / dnpvR;
      if (!isFinite(newR)) break;
      if (Math.abs(newR - r) < tolerance) return newR;
      r = newR;
      if (r < -0.99 || r > 10) break;
    }
    return bisectIRR(cashflows);
  }

  function bisectIRR(cashflows) {
    var lo = -0.99, hi = 10;
    var npvLo = npv(cashflows, lo);
    var npvHi = npv(cashflows, hi);
    if (npvLo * npvHi > 0) return NaN;
    for (var i = 0; i < 200; i++) {
      var mid = (lo + hi) / 2;
      var npvMid = npv(cashflows, mid);
      if (Math.abs(npvMid) < 1e-4) return mid;
      if (npvLo * npvMid < 0) { hi = mid; npvHi = npvMid; }
      else                    { lo = mid; npvLo = npvMid; }
    }
    return (lo + hi) / 2;
  }

  function npv(cashflows, rate) {
    var result = 0;
    for (var t = 0; t < cashflows.length; t++) result += cashflows[t] / Math.pow(1 + rate, t);
    return result;
  }

  /* ──────────────────────────────────────────────────────────
     Email dialog
     ────────────────────────────────────────────────────────── */
  function bindEmailDialog(ctx) {
    var dialog = ctx.el.querySelector('[data-roi-email-dialog]');
    var trig = ctx.el.querySelector('[data-roi-email-trigger]');
    var form = ctx.el.querySelector('[data-roi-email-form]');
    var emailInput = ctx.el.querySelector('[data-roi-email-input]');
    var operatorInput = ctx.el.querySelector('[data-roi-operator-type]');
    var closeBtn = ctx.el.querySelector('[data-roi-email-close]');
    var successEl = ctx.el.querySelector('[data-roi-email-success]');

    if (!dialog || !trig || !form) return;

    trig.addEventListener('click', function () {
      openDialog(dialog);
      track('nook_roi_email_open', { mode: ctx.mode });
    });
    if (closeBtn) closeBtn.addEventListener('click', function () { closeDialog(dialog, successEl, form); });
    dialog.addEventListener('click', function (e) { if (e.target === dialog) closeDialog(dialog, successEl, form); });

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var email = (emailInput && emailInput.value || '').trim();
      if (!email || email.indexOf('@') === -1) return;
      var payload = Object.assign(
        { email: email, operator_type: operatorInput ? operatorInput.value : '', mode: ctx.mode, currency: CURRENCY },
        snapshotSummary(ctx)
      );
      track('nook_roi_email_request', payload);
      track('generate_lead', { lead_source: 'roi_calculator', email: email, operator_type: payload.operator_type, mode: ctx.mode });
      if (successEl) successEl.hidden = false;
      setTimeout(function () { closeDialog(dialog, successEl, form); }, 2600);
    });
  }

  function openDialog(d) {
    if (typeof d.showModal === 'function') d.showModal();
    else d.setAttribute('open', '');
  }
  function closeDialog(d, successEl, form) {
    if (typeof d.close === 'function') d.close();
    else d.removeAttribute('open');
    if (successEl) successEl.hidden = true;
    if (form) form.reset();
  }

  function snapshotSummary(ctx) {
    var summary = { scenario: ctx.state.scenario };

    /* Sessions has no unit mix and no nightly-let economics — never let the
       lead payload report nightly figures from a per-session page. */
    if (ctx.mode === 'sessions') {
      var sres = computeSessions(ctx);
      summary.total_investment = formatMoney(sres.totalInvestmentCents);
      summary.buildings_list_price = formatMoney(sres.capexCents);
      summary.install_quote = formatMoney(sres.installQuoteCents);
      summary.site_allowance = formatMoney(sres.siteAllowanceCents);
      summary.booked_slots_per_week = formatSlots(sres.bookedPerWeek);
      summary.gross_revenue_y1 = formatMoney(sres.grossAnnualCents);
      summary.net_session_income_y1 = formatMoney(sres.netAnnualCents);
      summary.contribution_per_booked_slot = formatMoney(sres.contributionCents);
      /* Thresholds round UP here too — the emailed summary must not disagree with
         the on-page card the operator just read. */
      summary.break_even_slots_per_week = formatSlotsUp(sres.breakEvenSlotsPerWeek);
      summary.slots_per_week_for_seven_year_payback = formatSlotsUp(sres.slotsPerWeekForSevenYears);
      summary.payback_months = sres.paybackMonths;
      /* Codex W2 r2: with no install quote the investment-dependent figures are
         incomplete; the lead summary says so instead of reading as definitive. */
      /* Field types stay unchanged (payback_months numeric); the caveat travels as
         its own field, in the page's language (the visible note's own text). */
      summary.install_quote_missing = sres.installQuoteCents === 0;
      if (summary.install_quote_missing) {
        var noteEl = ctx.el.querySelector('[data-roi-s-incomplete]');
        summary.install_quote_caveat = noteEl ? noteEl.textContent.replace(/^\s*\*\s*/, '').trim() : '';
      }
      return summary;
    }

    summary.unit_mix = JSON.stringify(ctx.state.rows);
    if (ctx.mode === 'simple') {
      var row = ctx.state.rows[0];
      summary.unit_type = (ctx.unitTypes[row.unitIndex] || {}).label || '';
      summary.quantity = row.qty;
      summary.nightly_rate = row.rate;
      summary.occupancy = row.occ;
    } else {
      var totals = computePortfolio(ctx, 1, 1);
      summary.total_investment = formatMoney(totals.totalInvestmentCents);
      summary.gross_revenue_y1 = formatMoney(totals.grossRevenueCents);
      summary.net_revenue_y1 = formatMoney(totals.netRevenueCents);
      summary.goppar = formatMoney(totals.gopparCents);
      summary.payback_months = totals.paybackMonths;
      if (ctx.mode === 'institutional') {
        var cfs = buildIRRCashflows(totals, ctx.state.financing);
        var irrVal = irrNewton(cfs, 0.1);
        summary.irr = isFinite(irrVal) ? (irrVal * 100).toFixed(2) + '%' : '—';
        summary.npv = formatMoney(npv(cfs, ctx.state.financing.discountRate / 100));
      }
    }
    return summary;
  }

  /* ──────────────────────────────────────────────────────────
     Analytics + URL overrides
     ────────────────────────────────────────────────────────── */
  function debounceTrack(ctx) {
    clearTimeout(ctx.trackTimer);
    ctx.trackTimer = setTimeout(function () {
      var payload = { mode: ctx.mode, scenario: ctx.state.scenario, row_count: ctx.state.rows.length, currency: CURRENCY };
      if (ctx.mode === 'sessions') {
        var sres = computeSessions(ctx);
        payload.total_investment = Math.round(sres.totalInvestmentCents / 100);
        payload.payback_months = sres.paybackMonths;
        payload.net_session_income_y1 = Math.round(sres.netAnnualCents / 100);
        payload.install_quote_missing = sres.installQuoteCents === 0;
      } else if (ctx.mode !== 'simple') {
        var totals = computePortfolio(ctx, 1, 1);
        payload.total_investment = Math.round(totals.totalInvestmentCents / 100);
        payload.payback_months = totals.paybackMonths;
      }
      track('nook_roi_calculate', payload);
    }, 1500);
  }

  function track(event, payload) {
    if (window.NookTrack) window.NookTrack.push(event, payload || {});
    else if (typeof window.dataLayer !== 'undefined') window.dataLayer.push(Object.assign({ event: event }, payload || {}));
  }

  function applyURLOverrides(ctx) {
    try {
      var params = new URLSearchParams(window.location.search);
      var mode = params.get('roi-mode') || params.get('mode');
      if (mode && ctx.enabledModes.indexOf(mode) !== -1) {
        ctx.mode = mode;
        ctx.state = defaultStateForMode(mode, ctx);
      }
      var preset = params.get('roi-preset') || params.get('preset');
      if (preset && PRESETS[preset]) applyPreset(ctx, preset);
      var scenario = params.get('roi-scenario') || params.get('scenario');
      if (scenario && SCENARIO_MULTS[scenario]) ctx.state.scenario = scenario;
    } catch (e) { /* ignore */ }
  }

  /* ──────────────────────────────────────────────────────────
     Helpers
     ────────────────────────────────────────────────────────── */
  function readI18N() {
    try {
      var root = document.getElementById('nook-i18n');
      if (!root) return {};
      return JSON.parse(root.textContent);
    } catch (e) { return {}; }
  }

  function getUnitPriceLocal(ctx, unitIndex) {
    var unit = ctx.unitTypes[unitIndex] || ctx.unitTypes[0];
    if (!unit) return 0;
    if (CURRENCY === 'GBP' && unit.priceGbpCents > 0) return unit.priceGbpCents;
    if (CURRENCY === 'USD' && unit.priceUsdCents > 0) return unit.priceUsdCents;
    var converted = Math.round(unit.priceEurCents * FX_RATE);
    if (FX_ROUND > 0) converted = Math.round(converted / FX_ROUND) * FX_ROUND;
    return converted;
  }

  function convertEURToLocalRate(eurAmount) {
    return Math.round(eurAmount * FX_RATE);
  }

  function currencySymbol() {
    switch (CURRENCY) {
      case 'EUR': return '€';
      case 'GBP': return '£';
      case 'USD': return '$';
      default: return CURRENCY;
    }
  }

  function formatMoney(cents) {
    if (!isFinite(cents)) return '—';
    var amount = Math.round(cents) / 100;
    try {
      return new Intl.NumberFormat(LOCALE.indexOf('en') === 0 ? 'en-GB' : 'es-ES', {
        style: 'currency',
        currency: CURRENCY,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount);
    } catch (e) {
      return currencySymbol() + ' ' + amount.toLocaleString();
    }
  }

  function formatPercent(ratio) {
    if (!isFinite(ratio)) return '—';
    var v = ratio * 100;
    var digits = Math.abs(v) < 10 ? 1 : 0;
    var out = v.toFixed(digits).replace(/\.0$/, '');
    if (LOCALE.indexOf('en') !== 0) out = out.replace('.', ',');
    return out + '%';
  }

  /* Session counts — one decimal, locale-aware separator. null / non-finite /
     negative all render as the em-dash placeholder (never Infinity or NaN). */
  function formatSlots(v) {
    if (v == null || !isFinite(v) || v < 0) return '—';
    var out = (Math.round(v * 10) / 10).toFixed(1).replace(/\.0$/, '');
    if (LOCALE.indexOf('en') !== 0) out = out.replace('.', ',');
    return out;
  }

  /* Underwriting THRESHOLDS round UP, never to nearest. A required 6.54 slots/week
     displayed as "6,5" sits below the level it claims to describe — the operator
     books to the printed figure and misses the threshold. Nearest-rounding is
     correct for OBSERVED quantities (bookedPerWeek), wrong for a bar you must clear. */
  function formatSlotsUp(v) {
    if (v == null || !isFinite(v) || v < 0) return '—';
    var out = (Math.ceil(v * 10) / 10).toFixed(1).replace(/\.0$/, '');
    if (LOCALE.indexOf('en') !== 0) out = out.replace('.', ',');
    return out;
  }

  function formatPayback(months) {
    if (!months || months <= 0) return '—';
    var mLabel = T.months || 'months';
    var moLabel = T.month || 'month';
    var yLabel = T.years || 'years';
    var yoLabel = T.year || 'year';
    if (months < 12) return months + ' ' + (months === 1 ? moLabel : mLabel);
    var y = Math.floor(months / 12);
    var m = months % 12;
    var parts = [y + ' ' + (y === 1 ? yoLabel : yLabel)];
    if (m > 0) parts.push(m + ' ' + (m === 1 ? moLabel : mLabel));
    return parts.join(' ');
  }

  function updateRangeFill(input) {
    if (!input) return;
    var min = parseFloat(input.min);
    var max = parseFloat(input.max);
    var val = parseFloat(input.value);
    if (isNaN(min) || isNaN(max) || max === min) return;
    var pct = ((val - min) / (max - min)) * 100;
    input.style.setProperty('--fill', pct + '%');
  }

  function renderBenchmark(panel) {
    var benchmarkEl = panel.querySelector('[data-roi-benchmark]');
    if (!benchmarkEl) return;
    var tpl = T.benchmarkTemplate; if (!tpl) return;
    function money(eurInt) { return formatMoney(convertEURToLocalRate(eurInt) * 100); }
    function rangeText(lowAttr, highAttr) {
      var low = parseInt(benchmarkEl.getAttribute(lowAttr), 10);
      var high = parseInt(benchmarkEl.getAttribute(highAttr), 10);
      if (!low || !high) return '';
      return money(low) + '–' + money(high);
    }
    benchmarkEl.textContent = tpl
      .replace('{glamping_label}', T.benchmarkGlamping || 'Glamping')
      .replace('{glamping_range}', rangeText('data-benchmark-glamping-low', 'data-benchmark-glamping-high'))
      .replace('{boutique_label}', T.benchmarkBoutique || 'Boutique hotel')
      .replace('{boutique_range}', rangeText('data-benchmark-boutique-low', 'data-benchmark-boutique-high'))
      .replace('{wellness_label}', T.benchmarkWellness || 'Wellness retreat')
      .replace('{wellness_range}', rangeText('data-benchmark-wellness-low', 'data-benchmark-wellness-high'));
  }

  function panelEl(ctx, mode) { return ctx.el.querySelector('[data-roi-panel="' + mode + '"]'); }
  function setText(root, sel, text) { var e = root.querySelector(sel); if (e) e.textContent = text; }
  function toggleEmpty(panel, isEmpty) {
    var empty = panel.querySelector('[data-roi-empty]');
    var body = panel.querySelector('[data-roi-body]');
    if (empty) empty.hidden = !isEmpty;
    if (body) body.hidden = isEmpty;
  }
  function clampInt(v, min, max, fallback) {
    var n = parseInt(v, 10);
    if (isNaN(n)) return fallback;
    return Math.max(min, Math.min(max, n));
  }
  function clampRatio(v) {
    if (!isFinite(v)) return 0;
    return Math.max(0, Math.min(1, v));
  }
})();
